"use client";
import { useState, useEffect } from "react";
import {
  Users,
  Cloud,
  Database,
  Heart,
  ShoppingCart,
  Server,
  Brain,
  TrendingUp,
  Zap,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  Code,
  Layers,
} from "lucide-react";
import { AnimatedGradientBackground, GradientPresets } from "@/components/Animation/GradientBackground";
import IconBox from "@/components/Icons/iconbox";

interface TechIcon {
  name: string;
  icon: any;
}

interface Specialism {
  id: string;
  icon: any;
  title: string;
  tagline: string;
  description: string;
  howWeDeliver: string[];
  techIcons: TechIcon[];
}

const specialismsData: Specialism[] = [
  {
    id: "customer-commercial",
    icon: ShoppingCart,
    title: "Customer & Commercial Transformation",
    tagline: "Reimagining value through insight, design, and technology.",
    description:
      "We help organisations rediscover what truly drives value for their customers and rebuild around it. From digital product innovation to pricing strategy and automated service delivery, we integrate commercial vision with human-centred design and data-driven precision. Our goal is to ensure every customer interaction is intelligent, efficient, and aligned with measurable business performance.",
    howWeDeliver: [
      "Map end-to-end journeys and design new commercial models around lifetime value.",
      "Automate key experience touchpoints with ServiceNow, Salesforce, and low-code workflow platforms.",
      "Integrate customer, product, and operational data to create a single intelligent view.",
      "Apply predictive analytics and ML forecasting to personalise engagement and pricing.",
    ],
    techIcons: [
      { name: "Salesforce", icon: Database },
      { name: "ServiceNow", icon: Server },
      { name: "Python ML", icon: Brain },
      { name: "AWS", icon: Cloud },
    ],
  },
  {
    id: "technology-platforms",
    icon: Server,
    title: "Technology & Platforms",
    tagline: "Digital infrastructure for sustained outcome.",
    description:
      "We modernise technology estates and operating models to unlock scalability and resilience. Our architects work across hybrid and multi-cloud environments to simplify legacy systems, improve data flow, and embed security by design. The result: flexible platforms that adapt as fast as your organisation does.",
    howWeDeliver: [
      "Define digital architecture strategy and governance frameworks.",
      "Lead migration from legacy systems to modular, API-driven architectures.",
      "Build secure integration layers using MuleSoft, AWS Lambda, and microservice APIs.",
      "Optimise operations with DevOps pipelines and automation tooling for continuous deployment.",
    ],
    techIcons: [
      { name: "AWS", icon: Cloud },
      { name: "Kubernetes", icon: Layers },
      { name: "Terraform", icon: Code },
      { name: "Databricks", icon: Database },
    ],
  },
  {
    id: "data-ai",
    icon: Brain,
    title: "Data & AI",
    tagline: "From data collection to intelligence creation.",
    description:
      "We help clients unlock the full value of their information assets. That means not just building data lakes or dashboards, but designing intelligent ecosystems where governance, analytics, and automation work together to drive confident decisions. As data consulting specialists, we bring deep expertise in data architecture, engineering, and AI ethics — ensuring output is as responsible as it is revolutionary.",
    howWeDeliver: [
      "Build secure data lakehouses and pipelines using Databricks, Snowflake, and AWS Glue.",
      "Develop machine learning models for forecasting, risk scoring, and optimisation.",
      "Establish data governance and cataloguing frameworks aligned with FAIR principles.",
      "Implement AI-assisted analytics with tools like Power BI, Tableau, and Python Streamlit apps for self-service insight.",
    ],
    techIcons: [
      { name: "Databricks", icon: Database },
      { name: "Snowflake", icon: Database },
      { name: "Python", icon: Code },
      { name: "Power BI", icon: TrendingUp },
    ],
  },
  {
    id: "people-change",
    icon: Heart,
    title: "People & Change",
    tagline: "Embedding transformation through capability and culture.",
    description:
      "Technology enables modernisation — but people sustain it. We work with leaders to build clarity of purpose, adaptive governance, and inclusive culture. By connecting behavioural insight with operating-model design, we help teams not only accept change but own it.",
    howWeDeliver: [
      "Conduct change diagnostics and leadership alignment programmes.",
      "Build transformation offices and internal change networks.",
      "Design capability uplift pathways using digital learning, AI assistants, and knowledge management platforms.",
      "Integrate culture and performance analytics to monitor adoption in real time.",
    ],
    techIcons: [
      { name: "ServiceNow", icon: Server },
      { name: "Workday", icon: Users },
      { name: "Miro", icon: Lightbulb },
      { name: "Viva Insights", icon: TrendingUp },
    ],
  },
];

const approachSteps = [
  {
    icon: Lightbulb,
    title: "Illuminate",
    description:
      "We uncover the real problem by analysing context, systems, and objectives to create a understanding of what success is.",
  },
  {
    icon: Layers,
    title: "Design",
    description:
      "We co-create strategies that connect ambition with practical delivery, ensuring solutions are built for real-world conditions.",
  },
  {
    icon: Zap,
    title: "Launch",
    description:
      "We deliver with disciplined agility, using transparent planning and data-led decisions to keep speed and accountability in balance.",
  },
  {
    icon: TrendingUp,
    title: "Embed",
    description:
      "We transfer knowledge, strengthen capability, and ensure change continues to grow after project is live.",
  },
];

export default function SpecialismsPage() {
  const [expandedSpecialism, setExpandedSpecialism] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      setExpandedSpecialism(hash);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []);

  const toggleSpecialism = (id: string) => {
    setExpandedSpecialism(expandedSpecialism === id ? null : id);
  };

  return (
    <main className="min-h-screen text-text-primary">
      <AnimatedGradientBackground {...GradientPresets.specialisms} />

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6">
        <div className="border border-standard pt-40 p-8 sm:p-12 transition-all duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)] opacity-100 translate-y-0">
          <h1 className="h1 pt-24 mb-6">
            What We <span className="text-text-secondary">Do</span>
          </h1>
          <p className="body-lg mb-6">
            Transformation only lasts when strategy, technology, and people move as one.
          </p>
          <p className="body-md">
            Luminar designs and delivers that connection, ensuring that ideas don't just look
            good on paper but thrive in practice. We combine business design with deep delivery
            engineering, creating the conditions for real, measurable change.
          </p>
        </div>
      </section>

      {/* SPECIALISMS GRID */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="space-y-6">
          {specialismsData.map((specialism, index) => {
            const isExpanded = expandedSpecialism === specialism.id;
            const SpecialismIcon = specialism.icon;

            return (
              <div
                key={specialism.id}
                id={specialism.id}
                className="border border-standard backdrop-blur-sm overflow-hidden transition-all hover:shadow-glass scroll-mt-24"
              >
                <button
                  onClick={() => toggleSpecialism(specialism.id)}
                  className="w-full p-6 sm:p-8 text-left flex items-start justify-between gap-4 sm:gap-6"
                >
                  <div className="flex items-start gap-4 sm:gap-6 flex-1">
                    <IconBox icon={SpecialismIcon} index={index} size="lg" />
                    <div className="flex-1">
                      <h2 className="h3 mb-2">{specialism.title}</h2>
                      <p className="body-md italic">{specialism.tagline}</p>
                    </div>
                  </div>
                  <div
                    className={`flex-shrink-0 transition-transform duration-300 ${
                      isExpanded ? "rotate-90" : ""
                    }`}
                  >
                    <ArrowRight className="w-6 h-6 sm:w-8 sm:h-8 text-accentcolourteal" />
                  </div>
                </button>

                {/* Expandable Content */}
                <div
                  className={`overflow-hidden transition-all duration-500 ${
                    isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 sm:px-8 pb-6 sm:pb-8 sm:ml-20 space-y-6">
                    {/* Description */}
                    <div>
                      <div
                        className={`h-1 w-12 mb-4 ${
                          index % 2 === 0 ? "bg-accentcolourteal" : "bg-accentcolourorange"
                        }`}
                      />
                      <p className="body-md leading-relaxed">{specialism.description}</p>
                    </div>

                    {/* How We Deliver */}
                    <div>
                      <h3 className={`h3 mb-4 ${
                          index % 2 === 0 ? "text-accentcolourteal" : "text-accentcolourorange"
                        }`}>
                        How We Deliver
                      </h3>
                      <ul className="space-y-3">
                        {specialism.howWeDeliver.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle
                              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                index % 2 === 0 ? "text-accentcolourteal" : "text-accentcolourorange"
                              }`}
                            />
                            <span className="body-md">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technologies */}
                    <div className="pt-4">
                      <h4 className="body-md font-semibold text-text-primary mb-4">
                        Technologies Used
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        {specialism.techIcons.map((tech, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col items-center justify-center p-3 sm:p-4 border border-standard backdrop-blur-sm rounded-lg min-h-[100px] sm:min-h-[120px]"
                          >
                            <IconBox
                              icon={tech.icon}
                              index={index}
                              size="sm"
                              className="mb-2"
                            />
                            <span className="body-md text-center break-words w-full">
                              {tech.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="card-glass">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="h2 mb-4">Our Approach</h2>
            <p className="body-lg">A Proven Delivery Rhythm</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {approachSteps.map((step, i) => (
              <div key={i} className="group relative border border-standard backdrop-blur-sm p-6">
                <div className="absolute top-4 right-4 text-5xl font-bold text-accentcolourteal/10">
                  {i + 1}
                </div>

                <IconBox icon={step.icon} index={0} variant="badge" size="md" className="mb-4 relative z-10" />

                <h3 className="h3 mb-3 relative z-10">{step.title}</h3>
                <p className="body-md leading-relaxed relative z-10">
                  {step.description}
                </p>

                <div className="mt-4 h-1 w-8 bg-accentcolourteal" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERY CAPABILITY */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="max-w-3xl">
          <div className="relative border border-standard backdrop-blur-sm p-8 sm:p-12 overflow-hidden group transition-all hover:border-accentcolourorange">
            <div className="absolute -right-8 -bottom-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <Code className="w-48 h-48 sm:w-64 sm:h-64 text-accentcolourorange" />
            </div>

            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-6 sm:mb-8">
                <IconBox icon={Code} index={1} variant="badge" size="md" />
                <div>
                  <h2 className="h2">Delivery Capability</h2>
                  <div className="h-0.5 w-24 bg-accentcolourorange mt-2" />
                </div>
              </div>

              <div>
                <p className="body-lg mb-4 sm:mb-6">
                  Every engagement is engineered for precision through automated CI/CD pipelines,
                  infrastructure as code, and DevSecOps practices.
                </p>
                <p className="body-md">
                  We blend consulting discipline with agile delivery and real-time telemetry
                  ensuring progress is visible, security is maintained, and results last long after
                  handover.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLLABORATION */}
      <section className="max-w-6xl mx-auto px-6 mt-6 mb-24">
        <div className="max-w-3xl sm:ml-auto">
          <div className="relative border border-standard backdrop-blur-sm p-8 sm:p-12 overflow-hidden group transition-all hover:border-accentcolourteal">
            <div className="absolute -left-8 -bottom-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <Users className="w-48 h-48 sm:w-64 sm:h-64 text-accentcolourteal" />
            </div>

            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-6 sm:mb-8">
                <IconBox icon={Users} index={0} variant="badge" size="md" />
                <div>
                  <h2 className="h2">Collaboration</h2>
                  <div className="h-0.5 w-24 bg-accentcolourteal mt-2" />
                </div>
              </div>

              <div>
                <p className="body-lg mb-4 sm:mb-6">
                  We work shoulder to shoulder with client teams, integrating seamlessly and
                  sharing full ownership of delivery.
                </p>
                <p className="body-md">
                  Knowledge transfer is continuous by project close, teams have not just new systems
                  but the confidence and skills to keep improving them independently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}